import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  GOLD,
  buildDayDomain,
  classifyBandBreach,
  fmtNumber,
  formatAmountVsTypical,
  formatMillions,
  money,
  scoreFactors,
  sortTransactionsByMagnitude,
  transactionDayIndex
} from './workbenchHelpers';

const empty = (message) => <div className="ci-empty-state">{message}</div>;

const periodKey = (period) => String(period || '').slice(0, 7);

const daysInPeriod = (period) => {
  const match = String(period || '').match(/^(\d{4})-(\d{2})/);
  if (!match) return 31;
  return new Date(Number(match[1]), Number(match[2]), 0).getDate();
};

const chartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ci-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((entry) => (
        <span key={entry.dataKey || entry.name}>
          {entry.name || entry.dataKey}: {money(entry.value)}
        </span>
      ))}
    </div>
  );
};

function SpinnerLabel({ active, children }) {
  return (
    <>
      {active && <span className="ci-spinner" aria-hidden="true" />}
      <span className={active ? 'ci-running-text' : ''}>{children}</span>
    </>
  );
}

function Panel({ eyebrow, title, children, className = '' }) {
  return (
    <section className={`ace-panel ci-workbench-panel ${className}`}>
      <div className="ace-eyebrow">{eyebrow}</div>
      {title && <h3>{title}</h3>}
      {children}
    </section>
  );
}

function Chip({ children, tone = '' }) {
  return <span className={`ace-pill ci-chip ${tone ? `ci-chip--${tone}` : ''}`}>{children}</span>;
}

function DecisionHeader({ finding, onJudge, judging }) {
  const factors = scoreFactors(finding);
  const scope = [
    finding?.planningEntity || finding?.entity,
    finding?.glGroup,
    finding?.glAccount,
    finding?.costCenter,
    finding?.measure
  ].filter(Boolean);

  return (
    <Panel eyebrow="Decision header" className="ci-decision-panel">
      <div className="ci-decision-head">
        <div>
          <div className="ci-scope-line">{scope.join(' · ') || 'Scope not returned'}</div>
          <div className="ci-score-equation">
            <span>z <strong>{fmtNumber(factors.z, 2)}</strong></span>
            <span>x log$ <strong>{fmtNumber(factors.materiality, 2)}</strong></span>
            <span>x weight <strong>{fmtNumber(factors.weight, 2)}</strong></span>
            <span className="ci-score-arrow">score <strong>{fmtNumber(finding?.score, 1)}</strong></span>
          </div>
        </div>
        <div className="ci-chip-row">
          <Chip tone="status">{finding?.status || 'open'}</Chip>
          <Chip tone="critical">{finding?.criticality || 'unjudged'}</Chip>
        </div>
      </div>
      <div className="ci-brief-block">
        {finding?.brief ? (
          <p>{finding.brief}</p>
        ) : (
          <div className="ci-judge-affordance">
            <span>No judgment brief yet.</span>
            <button
              type="button"
              className="ace-pill ci-pinpoint-button"
              disabled={judging}
              onClick={() => onJudge(finding)}
            >
              <Sparkles size={13} />
              <SpinnerLabel active={judging}>{judging ? 'Judging' : 'Run judge'}</SpinnerLabel>
            </button>
          </div>
        )}
      </div>
    </Panel>
  );
}

function HistorySection({ context }) {
  const monthly = context?.monthly || {};
  const cell = Array.isArray(monthly.cell) ? monthly.cell : [];
  const sameMonth = Array.isArray(monthly.sameMonth) ? monthly.sameMonth : [];
  const sameMonthPeriods = new Set(sameMonth.map((row) => periodKey(row.period)));
  const baseline = monthly.baseline || {};
  const median = Number(baseline.median);
  const asOfPeriod = periodKey(context?.finding?.asOfPeriod || context?.finding?.as_of_period || cell[cell.length - 1]?.period);
  const asOfCell = cell.find((row) => periodKey(row.period) === asOfPeriod) || cell[cell.length - 1];
  const breach = classifyBandBreach(asOfCell?.value, baseline);
  const chartData = cell.slice(-36).map((row, index, rows) => {
    const key = periodKey(row.period);
    const isSameMonth = sameMonthPeriods.has(key);
    const isAsOf = key === asOfPeriod || (!asOfPeriod && index === rows.length - 1);
    return {
      period: key,
      value: Number(row.value) || 0,
      isSameMonth,
      sameMonthValue: isSameMonth ? Number(row.value) || 0 : null,
      medianSameMonth: isSameMonth && Number.isFinite(median) ? median : null,
      isAsOf
    };
  });

  return (
    <Panel eyebrow="History" title="Is this out of line?">
      {!chartData.length ? (
        empty('No monthly history returned for this finding yet.')
      ) : (
        <>
          <div className="ci-chart-box">
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 12, right: 18, left: 6, bottom: 28 }}>
                <CartesianGrid stroke="var(--ace-line)" vertical={false} />
                <XAxis
                  dataKey="period"
                  interval="preserveStartEnd"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                />
                <YAxis tickFormatter={money} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} width={56} />
                {breach.lower !== null && breach.upper !== null && (
                  <ReferenceArea
                    y1={breach.lower}
                    y2={breach.upper}
                    fill="color-mix(in srgb, hsl(var(--foreground)) 9%, transparent)"
                    strokeOpacity={0}
                  />
                )}
                <Tooltip content={chartTooltip} cursor={{ fill: 'color-mix(in srgb, var(--ace-line) 35%, transparent)' }} />
                <Bar dataKey="value" name="cell" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                  {chartData.map((row) => (
                    <Cell
                      key={row.period}
                      fill={
                        row.isAsOf
                          ? breach.breached
                            ? 'var(--ace-negative)'
                            : 'var(--ace-positive)'
                          : 'color-mix(in srgb, hsl(var(--foreground)) 46%, transparent)'
                      }
                    />
                  ))}
                </Bar>
                <Line
                  type="linear"
                  dataKey="medianSameMonth"
                  name="same-month median"
                  stroke={GOLD}
                  strokeWidth={1.5}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Scatter dataKey="sameMonthValue" name="same month" fill={GOLD} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="ci-mono-strip">
            same month across years: {sameMonth.map((row) => formatMillions(row.value)).join(' · ') || 'n/a'}
          </div>
        </>
      )}
    </Panel>
  );
}

function IntramonthSection({ context }) {
  const [mode, setMode] = useState('cumulative');
  const months = Array.isArray(context?.daily?.months) ? context.daily.months : [];
  const pattern = context?.daily?.pattern || {};
  const domain = buildDayDomain(months, context?.finding?.asOfPeriod);
  const rows = domain.days.map((day) => {
    const row = { day };
    months.forEach((month, index) => {
      const key = month.isAsOf ? 'asOf' : `m${index}`;
      const point = (month.days || []).find((item) => Number(item.day) === day);
      row[key] = point ? Number(point[mode]) || 0 : null;
    });
    return row;
  });
  const comparisonMonths = months
    .map((month, index) => ({ ...month, key: month.isAsOf ? 'asOf' : `m${index}` }))
    .filter((month) => !month.isAsOf);
  const asOfMonth = months.find((month) => month.isAsOf);
  const isSmooth = pattern.bucket === 'smooth';

  return (
    <Panel eyebrow="Intramonth build" title="Is the spike structural?">
      <div className="ci-section-actions">
        <div className="ci-segmented" aria-label="Intramonth chart mode">
          {['cumulative', 'daily'].map((option) => (
            <button
              key={option}
              type="button"
              className={mode === option ? 'active' : ''}
              onClick={() => setMode(option)}
            >
              {option}
            </button>
          ))}
        </div>
        {pattern.bucket && (
          <Chip tone={isSmooth ? 'positive' : 'ink'}>{isSmooth ? 'Smooth accruer' : 'Batch-fed'}</Chip>
        )}
      </div>
      {!months.length ? (
        empty('No intramonth build pattern returned yet.')
      ) : (
        <>
          <div className="ci-chart-box">
            <ResponsiveContainer>
              <LineChart data={rows} margin={{ top: 12, right: 18, left: 6, bottom: 24 }}>
                <CartesianGrid stroke="var(--ace-line)" vertical={false} />
                <XAxis
                  dataKey="day"
                  type="number"
                  domain={[1, domain.maxDay]}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                />
                <YAxis tickFormatter={money} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} width={56} />
                <Tooltip content={chartTooltip} />
                {domain.showCloseMarker && (
                  <ReferenceLine x={domain.closeDay} stroke="var(--ace-line)" strokeDasharray="3 3">
                    <Label value="close" position="insideTopRight" fill="hsl(var(--muted-foreground))" fontSize={10} />
                  </ReferenceLine>
                )}
                {comparisonMonths.map((month) => (
                  <Line
                    key={month.key}
                    type="monotone"
                    dataKey={month.key}
                    name={periodKey(month.period)}
                    stroke="hsl(var(--foreground))"
                    strokeOpacity={0.35}
                    strokeWidth={1}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
                {asOfMonth && (
                  <Line
                    type="monotone"
                    dataKey="asOf"
                    name={periodKey(asOfMonth.period) || 'as-of'}
                    stroke={GOLD}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {pattern.note && <p className="ci-caption">{pattern.note}</p>}
        </>
      )}
    </Panel>
  );
}

function RecurrenceSection({ context, recurrenceRefs }) {
  const rows = Array.isArray(context?.similar) ? context.similar : [];
  const asOfMonth = periodKey(context?.finding?.asOfPeriod || context?.finding?.as_of_period).slice(5, 7);

  return (
    <Panel eyebrow="Recurrence" title="Have we seen this before?" className="ci-recurrence-panel">
      {!rows.length ? (
        empty('No recurrence evidence returned yet.')
      ) : (
        <div className="ci-recurrence-list">
          {rows.map((row) => {
            const occurrences = Array.isArray(row.occurrences) ? row.occurrences.slice(-24) : [];
            return (
              <article
                key={row.key || row.label}
                ref={(node) => {
                  if (node && row.key) recurrenceRefs.current[row.key] = node;
                }}
                className="ci-recurrence-row"
                data-similar-key={row.key}
              >
                <div>
                  <strong>{row.label || row.key || 'Similar transaction'}</strong>
                  <div className="ci-chip-row">
                    {row.isAnnualPattern && <Chip tone="annual">Annual pattern</Chip>}
                    {row.isMonthlyPattern && <Chip tone="ink">Monthly pattern</Chip>}
                  </div>
                  <div className="ci-mono-facts">
                    seen {fmtNumber(row.monthsSeen, 0)}x across {fmtNumber(row.distinctYears, 0)}y · typical {formatMillions(row.typicalAmount)} · this month {formatAmountVsTypical(row.amountVsTypicalPct)}
                  </div>
                </div>
                <div className="ci-mini-timeline" aria-label="Occurrence timeline">
                  {occurrences.map((occurrence, index) => (
                    <span
                      key={`${occurrence.period}-${occurrence.postingDate}-${index}`}
                      className={periodKey(occurrence.period).slice(5, 7) === asOfMonth ? 'same-month' : ''}
                      title={`${periodKey(occurrence.period)} ${money(occurrence.amount)}`}
                    />
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function TransactionsSection({ context, recurrenceRefs }) {
  const transactions = sortTransactionsByMagnitude(context?.transactions);
  const similarByKey = new Map((context?.similar || []).map((row) => [row.key, row]));

  const scrollToRecurrence = (key) => {
    const node = recurrenceRefs.current[key];
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Panel eyebrow="Transactions">
      {!transactions.length ? (
        empty('No transactions returned for this finding yet.')
      ) : (
        <div className="ci-table-wrap">
          <table className="ci-transaction-table">
            <thead>
              <tr>
                {['Date', 'Day', 'Type', 'Vendor/Memo', 'Amount', 'Flags'].map((head) => (
                  <th key={head}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map((txn) => {
                const dayIndex = transactionDayIndex(txn);
                const postingDate = txn.postingDate || txn.posting_date || '';
                const daysInMonth = daysInPeriod(postingDate);
                const similar = txn.similarKey ? similarByKey.get(txn.similarKey) : null;
                const amount = Number(txn.amount) || 0;
                return (
                  <tr key={txn.id || `${postingDate}-${txn.amount}-${txn.memo}`}>
                    <td>{postingDate || 'n/a'}</td>
                    <td className="ci-mono">{dayIndex ?? 'n/a'}</td>
                    <td>{txn.txnType || txn.txn_type || 'n/a'}</td>
                    <td>
                      <strong>{txn.vendor || 'Vendor n/a'}</strong>
                      <span>{String(txn.memo || '').slice(0, 60)}</span>
                    </td>
                    <td className={`ci-mono ci-amount ${amount < 0 ? 'negative' : ''}`}>{money(amount)}</td>
                    <td>
                      <div className="ci-chip-row">
                        {txn.isTopMover && <Chip tone="critical">Top mover</Chip>}
                        {dayIndex > daysInMonth && <Chip tone="ink">Post-close</Chip>}
                        {similar && (
                          <button type="button" className="ace-pill ci-mini-link" onClick={() => scrollToRecurrence(txn.similarKey)}>
                            ≈ {similar.label}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function DispositionBar({ finding, onSaved }) {
  const [verdict, setVerdict] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!verdict || !note.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onSaved(finding, { verdict, note: note.trim() });
      setVerdict('');
      setNote('');
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ci-disposition-bar">
      <div className="ci-disposition-actions">
        {[
          ['real', 'Real anomaly — keep surfacing this pattern'],
          ['noise', 'One-off noise — teach the monitor to suppress this pattern'],
          ['known_recurring', 'Expected recurring item — an annual/monthly rule is drafted']
        ].map(([option, tip]) => (
          <button
            key={option}
            type="button"
            className={`ace-pill ci-tip ${verdict === option ? 'ace-pill--active' : ''}`}
            data-tip={tip}
            onClick={() => setVerdict(option)}
          >
            {option.replace('_', ' ')}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Required disposition note"
        rows={2}
      />
      <button
        type="button"
        className="ace-pill ci-pinpoint-button ci-tip"
        data-tip="Record the verdict — the queue advances to the next open finding"
        disabled={busy || !verdict || !note.trim()}
        onClick={save}
      >
        <CheckCircle2 size={13} />
        <SpinnerLabel active={busy}>{busy ? 'Saving' : 'Save'}</SpinnerLabel>
      </button>
      {error && <small className="ci-error">{error}</small>}
    </div>
  );
}

export default function FindingWorkbench({
  finding,
  context,
  contextState,
  onRetryContext,
  onJudge,
  judging,
  onDispositionSaved
}) {
  const mergedContext = useMemo(() => ({ ...(context || {}), finding: context?.finding || finding }), [context, finding]);
  const recurrenceRefs = useRef({});

  if (!finding) {
    return <section className="ace-panel ci-workbench-empty">No findings returned for this period.</section>;
  }

  return (
    <div className="ci-workbench-pane">
      {contextState?.status === 'retry' && (
        <div className="ci-context-retry">
          Context endpoint is not available yet.
          <button type="button" className="ace-pill" onClick={onRetryContext}>
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}
      {contextState?.status === 'error' && (
        <div className="ci-context-retry">
          Context could not be loaded.
          <button type="button" className="ace-pill" onClick={onRetryContext}>
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}
      {contextState?.status === 'loading' && <div className="ci-context-retry">Loading context…</div>}
      <DecisionHeader finding={finding} onJudge={onJudge} judging={judging} />
      <HistorySection context={mergedContext} />
      <IntramonthSection context={mergedContext} />
      <RecurrenceSection context={mergedContext} recurrenceRefs={recurrenceRefs} />
      <TransactionsSection context={mergedContext} recurrenceRefs={recurrenceRefs} />
      <DispositionBar finding={finding} onSaved={onDispositionSaved} />
    </div>
  );
}
